/**
 * Formatage de dates suivant la langue active de l'app (i18n).
 *
 * Pourquoi : les dates V2 étaient formatées en dur avec
 * `toLocaleDateString("fr-BE")`, donc les noms de mois ("juin", "mars", …)
 * et jours de la semaine restaient en français même en mode NL/EN.
 * Ce helper mappe la langue applicative (`i18n.language`) vers une locale
 * BCP-47 afin que Intl produise les bons noms de mois/jours.
 *
 * Usage typique dans un composant :
 *   const { t, i18n } = useTranslation();
 *   formatDate(bien.finDeContrat, i18n.language, { month: "long", year: "numeric" });
 *
 * Pour les helpers de formatage définis au niveau module (hors composant),
 * passer `lng` en paramètre et laisser le composant transmettre `i18n.language`.
 */

/**
 * Mappe la langue applicative ("fr" | "nl" | "en", éventuellement avec suffixe
 * régional comme "fr-BE") vers une locale BCP-47.
 * Fallback : "fr-BE".
 */
export function localeFromLng(lng: string): string {
  const base = (lng || "").slice(0, 2).toLowerCase();
  switch (base) {
    case "nl":
      return "nl-BE";
    case "en":
      return "en-GB";
    case "fr":
      return "fr-BE";
    default:
      return "fr-BE";
  }
}

/**
 * Formate une date selon la langue active.
 * Renvoie "" pour null/undefined/"" et pour une date invalide : les call sites
 * conservent ainsi leur propre fallback ("—", null, valeur brute, …).
 * Accepte une string (parsée en Date) ou un objet Date.
 */
export function formatDate(
  value: string | Date | null | undefined,
  lng: string,
  opts?: Intl.DateTimeFormatOptions,
): string {
  if (value == null || value === "") return "";
  const d = value instanceof Date ? value : new Date(value);
  if (isNaN(d.getTime())) return "";
  return d.toLocaleDateString(localeFromLng(lng), opts);
}
